/**
 * URL → state hydration for the Pilot app. Phase J of the imperative-
 * renderer retirement (docs/eng/pilot-engine-imperative-retirement.md)
 * lifts main.js's `hydrateIdentityFromUrl` into a React-friendly,
 * pre-mount side-effect module.
 *
 * Two flavors of URL state are honored:
 *
 *  1. Sales personalization — `?name=…&email=…&company=…&role=…&linkedin=…`
 *     stashed into `mp-talk-lead` (+ `mp-email`) so the talk modal can
 *     pre-fill on open.
 *  2. Share-link deep state — `?process=<id>&industry=…&size=…&function=…
 *     &avgResponseHrs=…&…` so a buyer can share "look at these numbers"
 *     with stakeholders. Intake fields merge into `mp-intake`; knob
 *     overrides merge into `mp-params`; the process id is held so
 *     PilotContext can adopt it on mount.
 *
 * `applyUrlStateToStorage()` MUST run **before** PilotContext is
 * mounted (the provider reads localStorage in its useState
 * initializers). Calling it from index.tsx ahead of
 * `ReactDOM.createRoot().render(...)` keeps the contract simple.
 */

import {KNOBS, KNOB_DEFAULTS} from './knobs';
import type {ParamId} from './params';

const TALK_LEAD_KEY = 'mp-talk-lead';
const EMAIL_KEY = 'mp-email';
const INTAKE_KEY = 'mp-intake';
const PARAMS_KEY = 'mp-params';

const IDENTITY_KEYS = ['name', 'email', 'company', 'role', 'linkedin'] as const;
const INTAKE_KEYS = ['industry', 'size', 'function'] as const;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

let _hydrated = false;
let _deepLinkProcessId: string | null = null;

/** True once `applyUrlStateToStorage` has run (no-op on repeat). */
export function isUrlHydrated(): boolean {
  return _hydrated;
}

/** The `?process=<id>` deep link value, if any was found at hydrate time. */
export function consumeUrlDeepLinkProcessId(): string | null {
  const id = _deepLinkProcessId;
  _deepLinkProcessId = null;
  return id;
}

/** Read the deep-link without clearing it (for tests/diagnostics). */
export function peekUrlDeepLinkProcessId(): string | null {
  return _deepLinkProcessId;
}

/**
 * Parse `window.location.search` once and merge the resulting state
 * into localStorage. Safe to call multiple times — the second call is
 * a no-op.
 *
 * Returns the deep-link process id if one was found, so the caller
 * can drive a one-shot view-mode change without waiting for
 * `consumeUrlDeepLinkProcessId`.
 */
export function applyUrlStateToStorage(): string | null {
  if (_hydrated) return _deepLinkProcessId;
  _hydrated = true;
  if (typeof window === 'undefined') return null;

  let search: URLSearchParams;
  try {
    search = new URLSearchParams(window.location.search);
  } catch {
    return null;
  }

  // ---- Identity (talk-lead + captured email) ----
  const identityStash: Record<string, string> = {};
  for (const k of IDENTITY_KEYS) {
    const v = search.get(k);
    if (v) identityStash[k] = v;
  }
  if (Object.keys(identityStash).length > 0) {
    try {
      const prev =
        JSON.parse(window.localStorage.getItem(TALK_LEAD_KEY) || '{}') || {};
      window.localStorage.setItem(
        TALK_LEAD_KEY,
        JSON.stringify({...prev, ...identityStash}),
      );
    } catch {
      /* private mode */
    }
    if (
      identityStash.email &&
      EMAIL_REGEX.test(identityStash.email.trim())
    ) {
      try {
        window.localStorage.setItem(EMAIL_KEY, identityStash.email.trim());
      } catch {
        /* private mode */
      }
    }
  }

  // ---- Intake ----
  const intakeStash: Record<string, string> = {};
  for (const k of INTAKE_KEYS) {
    const v = search.get(k);
    if (v) intakeStash[k] = v;
  }
  if (Object.keys(intakeStash).length > 0) {
    try {
      const prev =
        JSON.parse(window.localStorage.getItem(INTAKE_KEY) || '{}') || {};
      window.localStorage.setItem(
        INTAKE_KEY,
        JSON.stringify({...prev, ...intakeStash}),
      );
    } catch {
      /* private mode */
    }
  }

  // ---- Knob overrides ----
  const overrides: Record<string, number> = {};
  for (const knob of KNOBS) {
    const v = search.get(knob.id);
    if (v == null) continue;
    const num = parseFloat(v);
    if (!Number.isFinite(num)) continue;
    overrides[knob.id] = num;
  }
  if (Object.keys(overrides).length > 0) {
    try {
      const raw = window.localStorage.getItem(PARAMS_KEY);
      const prev =
        raw && typeof raw === 'string' ? JSON.parse(raw) : {...KNOB_DEFAULTS};
      const merged = {...(prev as Record<string, number>), ...overrides};
      window.localStorage.setItem(PARAMS_KEY, JSON.stringify(merged));
    } catch {
      /* private mode / malformed */
    }
  }

  // ---- Process deep-link ----
  const procId = search.get('process');
  if (procId) _deepLinkProcessId = procId;

  return _deepLinkProcessId;
}

/**
 * Build a shareable URL containing the current sandbox state — process
 * + intake + non-default knobs. Used by the "🔗 Share these numbers"
 * button and the talk modal's share input.
 */
export interface ShareUrlState {
  processId?: string | null;
  intake?: {industry?: string; size?: string; function?: string} | null;
  knobs?: Partial<Record<ParamId, number>> | null;
  identity?: {
    name?: string;
    email?: string;
    company?: string;
    role?: string;
    linkedin?: string;
  } | null;
}

export function buildSandboxShareUrl(state: ShareUrlState): string {
  if (typeof window === 'undefined') return '';
  const url = new URL(window.location.href);
  url.search = '';
  if (state.processId) url.searchParams.set('process', state.processId);
  if (state.intake) {
    for (const k of INTAKE_KEYS) {
      const v = state.intake[k];
      if (v) url.searchParams.set(k, v);
    }
  }
  if (state.knobs) {
    for (const knob of KNOBS) {
      const v = state.knobs[knob.id];
      if (v == null) continue;
      if (Math.abs(v - KNOB_DEFAULTS[knob.id]) <= 1e-6) continue;
      url.searchParams.set(knob.id, String(v));
    }
  }
  if (state.identity) {
    for (const k of IDENTITY_KEYS) {
      const v = state.identity[k];
      if (v) url.searchParams.set(k, v);
    }
  }
  return url.toString();
}

/**
 * Legacy buildShareUrl shape — used by main.js's talk-modal flow which
 * passes a flat `{...intake, ...lead}` object. Kept for back-compat
 * until the talk modal moves to React (Phase I).
 */
export function buildShareUrl(state: Record<string, string | undefined>): string {
  if (typeof window === 'undefined') return '';
  const url = new URL(window.location.href);
  url.search = '';
  const keys = [
    'industry',
    'size',
    'function',
    'name',
    'email',
    'company',
    'role',
    'linkedin',
  ];
  for (const k of keys) {
    const v = state[k];
    if (v) url.searchParams.set(k, v);
  }
  return url.toString();
}
